import type { DistanceGapReasonCode, DistanceNotComparableCode, DistanceResolutionOutcome, DistanceStateCode, DistanceTripItem } from '@chukua/common';
import type { CloseOutTab } from '../distance-agreement.paths';

type Distances = Pick<DistanceTripItem, 'odometerDistanceKm' | 'gpsDistanceKm' | 'gapKm' | 'reason' | 'notComparableReason'>;

export const NOT_AVAILABLE = 'Not available';

export const NOT_COMPARABLE_LABELS: Record<DistanceNotComparableCode, string> = {
  no_dispatch_reading: 'No dispatch reading: the truck had no pre-trip check',
  no_close_out_reading: 'No close-out reading: the trip was not received at the yard',
  no_gps_distance: 'No GPS distance yet',
  low_gps_coverage: 'GPS covers too little of the trip to judge it',
};

export const GAP_REASON_LABELS: Record<DistanceGapReasonCode, string> = {
  beyond_tolerance: 'The odometer and GPS distances differ by more than the tolerance',
  below_dispatch_reading: 'The close-out reading is below the dispatch reading',
  below_truck_odometer: "The close-out reading is below the truck's own odometer",
};

export const OUTCOME_LABELS: Record<DistanceResolutionOutcome, string> = {
  accepted: 'Accepted',
  gps_adjusted: 'GPS adjusted',
  odometer_error_noted: 'Odometer error noted',
};

export const OUTCOME_HINTS: Record<DistanceResolutionOutcome, string> = {
  accepted: 'The gap is understood and both figures stand. The close-out reading goes to the truck.',
  gps_adjusted: "The GPS figure is wrong (a tracker that was off, say). The odometer distance becomes the trip's distance, and the reading goes to the truck.",
  odometer_error_noted: 'The reading was misread. It is not written to the truck.',
};

const STATE_TAGS: Record<DistanceStateCode, { label: string; color: 'error' | 'success' | 'default' | 'processing' }> = {
  gap: { label: 'Gap', color: 'error' },
  resolved: { label: 'Resolved', color: 'success' },
  agrees: { label: 'Agrees', color: 'success' },
  awaiting_gps: { label: 'Waiting for GPS', color: 'processing' },
  not_comparable: { label: 'Not comparable', color: 'default' },
};

export const stateTag = (state: DistanceStateCode) => STATE_TAGS[state];

/** The state each tab shows; `all` leaves it to the table's own filter. */
export const TAB_STATES: Record<CloseOutTab, DistanceStateCode[] | undefined> = { gaps: ['gap'], resolved: ['resolved'], all: undefined };

/** Why the odometer distance is missing, or null when it is there. Never 0. */
export function odometerUnavailable(item: Distances): string | null {
  if (item.odometerDistanceKm !== null) return null;
  if (item.reason === 'below_dispatch_reading') return GAP_REASON_LABELS.below_dispatch_reading;
  if (item.notComparableReason === 'no_dispatch_reading' || item.notComparableReason === 'no_close_out_reading')
    return NOT_COMPARABLE_LABELS[item.notComparableReason];
  return 'No odometer distance';
}

export const gpsUnavailable = (item: Distances): string | null => (item.gpsDistanceKm === null ? NOT_COMPARABLE_LABELS.no_gps_distance : null);

/** Why there is no gap to show: whichever distance is missing, in that order. */
export const gapUnavailable = (item: Distances): string | null => (item.gapKm === null ? (odometerUnavailable(item) ?? gpsUnavailable(item)) : null);

/** The sentence under a trip's state: why it is a gap, or why it cannot be one. */
export function stateExplanation(item: Pick<DistanceTripItem, 'reason' | 'notComparableReason'>): string | null {
  if (item.reason) return GAP_REASON_LABELS[item.reason];
  return item.notComparableReason ? NOT_COMPARABLE_LABELS[item.notComparableReason] : null;
}

export const formatKm = (km: number): string => `${km.toLocaleString('en-US', { maximumFractionDigits: 1 })} km`;

export const formatPercent = (percent: number): string => `${percent.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;

/** Outcomes a resolver may choose: a reading below the dispatch reading is only ever a misread (the API refuses the others). */
export function outcomesFor(reason: DistanceGapReasonCode | null): DistanceResolutionOutcome[] {
  return reason === 'below_dispatch_reading' ? ['odometer_error_noted'] : ['accepted', 'gps_adjusted', 'odometer_error_noted'];
}

export const resolvedToast = (tripNumber: string): string => `Distance gap on ${tripNumber} resolved`;
